import "react-contexify/ReactContexify.css";

import { faSpinner } from "@fortawesome/pro-duotone-svg-icons";
import { faAngleDown, faAngleRight } from "@fortawesome/pro-regular-svg-icons";
import { faUpRightAndDownLeftFromCenter } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import { Editor, JSONContent } from "@tiptap/core";
import classNames from "classnames";
import { memo, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { yXmlFragmentToProsemirrorJSON } from "y-prosemirror";

import { django } from "@/api";
import { useActiveEditor } from "@/hooks";
import useQueries from "@/hooks/useQueries";
import { getPageIds } from "@/hooks/useQueries/pages";

import { loadExtensions } from "../../extensions/loadExtensions";
import styles from "../../extensions/RoadmapList/RoadmapList.module.scss";
import { convertXmlToYjs } from "../../extensions/RoadmapList/xmlToYjsConverter";

function findBulletList(json: JSONContent): JSONContent | null {
  for (const item of json.content ?? []) {
    if (item.type === "bulletList") {
      return item;
    }
    if (item.content && item.content.length > 0) {
      const result = findBulletList(item.content);
      if (result) {
        return result;
      }
    }
  }
  return null;
}

const collapsedStore = () => {
  let items: string | null = localStorage.getItem("disco-collapsed");
  let parsedItems: { [key: string]: boolean } = {};
  if (!items) {
    parsedItems = {};
  } else {
    parsedItems = JSON.parse(`${items}`);
  }
  return parsedItems;
};

export const storeCollapsed = (id: string, collapsed: boolean) => {
  const items = collapsedStore();
  items[id] = collapsed;
  localStorage.setItem("disco-collapsed", JSON.stringify(items));
};

export const getCollapsed = (id: string) => {
  const items = collapsedStore();
  return id in items ? items[id] : false;
};

const syncedCollapsedStore = () => {
  let items: string | null = localStorage.getItem("disco-syncedCollapsed");
  let parsedItems: { [key: string]: boolean } = {};
  if (!items) {
    parsedItems = {};
  } else {
    parsedItems = JSON.parse(`${items}`);
  }
  return parsedItems;
};

export const storeSyncedCollapsed = (id: string, syncedCollapsed: boolean) => {
  const items = syncedCollapsedStore();
  items[id] = syncedCollapsed;
  localStorage.setItem("disco-syncedCollapsed", JSON.stringify(items));
};

export const getSyncedCollapsed = (id: string) => {
  const items = syncedCollapsedStore();
  return id in items ? items[id] : true;
};

export const ListItemReadOnly = memo(
  ({
    children,
    pageId,
    node,
  }: {
    children: React.ReactNode;
    pageId: string;
    node: JSONContent;
  }) => {
    const navigate = useNavigate();
    let { projectPublicId } = useParams();

    const [syncedNodeId, setSyncedNodeId] = useState();
    const blockId = node?.attrs?.blockId;

    const [hover, setHover] = useState(false);
    const [hasChildList, setHasChildList] = useState(false);
    const [hasNodePage, setHasNodePage] = useState(false);
    const [collapsed, setCollapsed] = useState(getCollapsed(blockId));
    const [syncedCollapsed, setSyncedCollapsed] = useState(
      getSyncedCollapsed(node?.attrs?.syncedBlockId)
    );
    const { currentProject } = useQueries();

    const { data: pageIds } = useQuery({
      queryKey: ["page-ids"],
      queryFn: ({ signal }) => getPageIds(signal, currentProject?.id),
      enabled: Boolean(currentProject?.id),
      initialData: [],
    });

    const extensions = loadExtensions(undefined, undefined, true);
    const [syncedText, setSyncedText] = useState("");
    const [syncedBullet, setSyncedBullet] = useState("");
    const editorRef = useRef<HTMLDivElement>(null);

    const { activeEditor, setActiveEditor } = useActiveEditor();

    const bulletOnClick = () => {
      if (projectPublicId && !hasNodePage) return;

      navigate(`/public/${currentProject?.publicID}/${blockId}`);
    };

    const collapseOnClick = async () => {
      storeCollapsed(blockId, !collapsed);
      setCollapsed(!collapsed);
      await setActiveEditor(undefined);
      setActiveEditor(activeEditor);
    };

    useEffect(() => {
      setHasChildList(Boolean(findBulletList(node)));
      setHasNodePage(Boolean(pageIds.includes(blockId)));
    }, [node, pageIds]);

    useEffect(() => {
      const getData = async () => {
        setSyncedNodeId(node?.attrs?.syncedBlockId);
        const response = await django.get(`editor/node/${node?.attrs?.syncedBlockProject}/`, {
          params: {
            format: "json",
            block: node?.attrs?.syncedBlockId,
          },
        });
        const xmlFragment = convertXmlToYjs(response.data);
        const editorJson = yXmlFragmentToProsemirrorJSON(xmlFragment);
        const mainLabel = editorJson.content[0].content[0].content
          .map((item) => item.text)
          .join(" ");
        const bulletList = editorJson.content[0].content[1];
        setSyncedBullet(bulletList);
        setSyncedText(mainLabel);

        if (bulletList) {
          const newDoc = {
            type: "doc",
            content: [bulletList],
          };

          new Editor({
            element: editorRef.current,
            extensions,
            editable: false,
            content: newDoc,
          });
        }
      };

      if (node?.attrs?.syncedBlockId && editorRef.current) {
        getData();
      }
    }, [node?.attrs?.syncedBlockId, editorRef]);

    if (node?.attrs?.syncedBlockId)
      return (
        <div>
          <li
            className={classNames(styles.syncedNode, styles.listItem, {
              [styles.collapsed]: collapsed,
            })}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            data-blockid={blockId}
          >
            <div className={styles.menu}>
              {syncedBullet ? (
                <button
                  onClick={async () => {
                    storeSyncedCollapsed(node?.attrs?.syncedBlockId, !syncedCollapsed);
                    setSyncedCollapsed(!syncedCollapsed);
                    await setActiveEditor(undefined);
                    setActiveEditor(activeEditor);
                  }}
                  title={"Show content"}
                >
                  <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} />
                </button>
              ) : (
                <></>
              )}
            </div>
            <div
              className={classNames(styles.bullet, {
                [styles.hasNodePage]: hasNodePage,
              })}
              contentEditable={false}
              onClick={bulletOnClick}
            >
              <div className={styles.dot} contentEditable={false}></div>
            </div>
            <div className={styles.nodeContent} contentEditable={false}>
              <div
                className={classNames(styles.syncedNodeLabel, {
                  [styles.soloSyncedNode]: Boolean(!syncedBullet || syncedCollapsed),
                })}
                contentEditable={false}
              >
                {syncedText ? syncedText : <FontAwesomeIcon icon={faSpinner} spin />}
              </div>
              <div
                ref={editorRef}
                contentEditable={false}
                style={{ display: !syncedCollapsed ? "block" : "none" }}
              ></div>
            </div>
          </li>
        </div>
      );

    return (
      <div>
        <li
          className={classNames(styles.listItem, {
            [styles.collapsed]: collapsed,
          })}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          data-blockid={blockId}
        >
          {hasChildList ? (
            <div
              className={styles.collapse}
              contentEditable={false}
              onClick={collapseOnClick}
              style={{
                display: collapsed || hover ? "flex" : "none",
              }}
            >
              <FontAwesomeIcon icon={collapsed ? faAngleRight : faAngleDown} />
            </div>
          ) : (
            <></>
          )}
          <div
            className={classNames(styles.bullet, {
              [styles.hasNodePage]: hasNodePage,
            })}
            contentEditable={false}
            onClick={bulletOnClick}
          >
            <div className={styles.dot} contentEditable={false}></div>
          </div>
          <div className={styles.nodeContent} style={{ display: "block" }}>
            {children}
          </div>
        </li>
      </div>
    );
  }
);

export default ListItemReadOnly;
